import React from 'react';
import SEO from '../components/SEO';
import './About.css';

const About = () => {
  const backgroundStyle = {
    background: `linear-gradient(
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 0.7) 100%
    ),
    url('/images/site-photos/BR1A1917.JPG') center 20% / cover no-repeat`
  };

  return (
    <div className="about">
      <SEO 
        title="About"
        description="The story of how I got here and where I'm going. Learn about my journey as an artist and my creative process."
        image="/images/site-photos/BR1A0307.JPG"
        url="/about"
      />
      <div className="about-background" style={backgroundStyle}></div>
      <div className="container">
        {/* Header */}
        <div className="about-header">
          <h1 className="about-title">ABOUT <span className="brick-font">Me</span></h1>
          <p className="about-subtitle">
            My journey as an artist and the stories that shape my work.
          </p>
        </div>

        {/* Hero Image */}
        <section className="about-hero">
          <img 
            src="/images/site-photos/BR1A1600.JPG" 
            alt="Artist hero image" 
            className="hero-image"
          />
        </section>

        {/* Artist Statement */}
        <section className="about-statement">
          <div className="statement-layout">
            <div className="statement-image">
              <img 
                src="/images/site-photos/BR1A1887.JPG" 
                alt="Artist portrait" 
                className="artist-portrait"
              />
            </div>
            <div className="statement-content">
              <h2 className="statement-title">Artist Statement</h2>
              <div className="statement-text">
                <p>
                  My art is a conversation between me and God, with you invited in.
                </p>
                <p>
                  Each piece carries parts of my journey: beauty and brokenness, laughter and deep quiet, color and silence. I don't create just to make something beautiful—I create to heal, to reveal, and to return what was never mine to begin with. Because at the core of all of this, it's about giving art back to the Creator.
                </p>
                <p>
                  I always start in prayer.<br/>
                  What is God showing me right now? What in my own life needs to be uncovered?<br/>
                  Sometimes it's joy. Sometimes it's grief. Sometimes I don't know what it is until the paint dries and I step back and feel it.
                </p>
                <p>
                  The process is messy: so many sketches, so many wrong turns, but even that has its own rhythm. When it flows, it flows. When it doesn't, I still trust it. Because I've learned that even in the quiet, God's still speaking. Even in the hard moments, good art can be created. Especially in the hard moments.
                </p>
                <p>
                  These aren't just my pieces. They're God's. And if they meet you where you are, if something in the colors or the lines or the layers feels like home, then they're yours, too.
                </p>
                <p>
                  I hope my work feels like a hug when you need it, a mirror when you're searching, and a reminder that your story is sacred—every single part of it.
                </p>
                <p>
                  Because we are His masterpieces. And when we create with Him, we're never alone.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Themes Section */}
        <section className="about-themes">
          <div className="themes-layout">
            <div className="themes-content">
              <h2 className="themes-title">Themes in My Work</h2>
              <div className="themes-grid">
                <div className="theme-item">
                  <h3 className="theme-name">Beauty</h3>
                  <p className="theme-description">
                    To me, beauty isn't about perfection—it's about presence. It's found in the unfiltered, in the scars, in the way light hits a face that's seen some things and still chooses joy. I paint beauty the way I see it: messy, honest, and full of God.
                  </p>
                </div>
                <div className="theme-item">
                  <h3 className="theme-name">God</h3>
                  <p className="theme-description">
                    Every piece I make starts with Him. He's in the colors, the pauses, the little details I didn't plan. My work is worship—sometimes loud, sometimes whispered. But always sacred. Always ours.
                  </p>
                </div>
                <div className="theme-item">
                  <h3 className="theme-name">Identity</h3>
                  <p className="theme-description">
                    I paint who I am… and who I'm still becoming. My roots, my story, my softness, my fight—they all show up in the layers. And I hope when you see it, something in you feels seen, too.
                  </p>
                </div>
                <div className="theme-item">
                  <h3 className="theme-name">Softness</h3>
                  <p className="theme-description">
                    There's power in tenderness. I don't hide my heart—I pour it out. Because being soft in a hard world? That's holy work. That's strength.
                  </p>
                </div>
              </div>
            </div>
            <div className="themes-image">
              <img 
                src="/images/site-photos/BR1A1792.JPG" 
                alt="Themes in my work" 
                className="themes-portrait"
              />
            </div>
          </div>
        </section>

        {/* Journey Section */}
        <section className="about-journey">
          <div className="journey-background">
            <img 
              src="/images/site-photos/BR1A1126.JPG" 
              alt="Journey background" 
              className="journey-bg-image"
            />
            <div className="journey-overlay"></div>
          </div>
          <div className="journey-content">
            <h2 className="journey-title">My Journey</h2>
            <div className="journey-text">
              <p>
                I grew up in the Bronx, raised by resilience, color, and faith. The rhythm of the city, the grit of the streets, and the quiet moments with God in between… that's where I learned to see beauty. Not just in people, or places, but in the process. In the waiting. In the mess.
              </p>
              <p>
                I didn't grow up painting. Art found me when I needed it most. In one of the hardest seasons of my life, I picked up a brush and tried to make sense of the pain. What came out was my first piece: God Sustaining the Stained. That's when I realized—this wasn't just therapy. This was worship.
              </p>
              <p>
                Now, creating is how I breathe. I start with prayer, let the mess happen, and somewhere along the way, God shows up. Over time, what started as healing for me has become a space of healing for others too. That's all I ever want—for people to feel seen, softened, and safe.
              </p>
              <p>
                I'm always exploring—new techniques, new layers, new ways to listen. But no matter how far I go, I stay rooted in where I came from: the Bronx, the beauty, and my belief. Every piece is part of a conversation I'm having with God, and I'm just grateful to share it with you.
              </p>
            </div>
          </div>
        </section>

        {/* Creative Process */}
        <section className="about-process">
          <div className="process-layout">
            <div className="process-image">
              <img 
                src="/images/site-photos/BR1A1342.JPG" 
                alt="Creative process" 
                className="process-portrait"
              />
              <img 
                src="/images/site-photos/BR1A1057.JPG" 
                alt="Creative process" 
                className="process-portrait"
              />
              <img 
                src="/images/site-photos/BR1A1108.JPG" 
                alt="Creative process" 
                className="process-portrait"
              />
            </div>
            <div className="process-content">
              <h2 className="process-title">Creative Process</h2>
              <div className="process-grid">
                <div className="process-step">
                  <div className="step-number">01</div>
                  <h3 className="step-title">Intention</h3>
                  <p className="step-description">
                    Every piece starts with prayer. Before I touch a brush, I sit with God and ask: what needs to come through? What do You want to say? That moment sets the tone for everything that follows.
                  </p>
                </div>
                <div className="process-step">
                  <div className="step-number">02</div>
                  <h3 className="step-title">Intuition</h3>
                  <p className="step-description">
                    I don't force anything. I let the piece speak. Sometimes the lines come easy, other times I wrestle, but I trust that if I stay open, the truth will reveal itself. Not planned, just… led.
                  </p>
                </div>
                <div className="process-step">
                  <div className="step-number">03</div>
                  <h3 className="step-title">Connection</h3>
                  <p className="step-description">
                    While I paint, I stay connected to the Spirit. It's not just about colors or technique; it's about staying in flow, letting God move through my hands. That's when the art becomes more than just mine.
                  </p>
                </div>
                <div className="process-step">
                  <div className="step-number">04</div>
                  <h3 className="step-title">Release</h3>
                  <p className="step-description">
                    When a piece feels complete, I let it go. I pray over it, bless it, and trust that it will land exactly where it's needed. Because I believe these pieces don't belong to me alone... they're part of something much bigger.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About; 